import { css } from 'styled-components'

export const getResponsiveFontSizeCSS = (
    minFontSize = 14,
    maxFontSize = 80
) => {
    /**
     * This function is used to generate a reposinve text size
     * based on viewport width, max and min font size in pixels.
     * It returns the calc() function which can be directly used
     * with the css font-size property.
     *
     * @param {number} [minFontSize=14] The min font size on phone (in pixel)
     * @param {number} [maxFontSize=80] The max font size on 4K screens (in pixel)
     * @return {string} The calc() function which can be used as css property value
     *
     * @example
     * getResponsiveFontSizeCSS(14,80)
     * returns => "calc(14px + (80 - 14) * ((100vw - 300px) / (2560 - 300)))"
     *
     */

    const minViewportWidth = 300 //px (small phone)
    const maxViewportWidth = 2560 //px (4K)

    return `calc(${minFontSize}px + (${maxFontSize} - ${minFontSize}) * ((100vw - ${minViewportWidth}px) / (${maxViewportWidth} - ${minViewportWidth})))`
}

const formatStyle = styles => {
    // Replace all comma with a semi-colon
    const replaceAllComma = styles.trim().replace(/,/g, ';')
    // Check if style ends with a semi-colon
    const endsWithSemicolon = replaceAllComma.endsWith(';')
    // Add a semi-colon if doesn't ends with a semi-colon
    const finalStyleFormat = endsWithSemicolon
        ? replaceAllComma
        : `${replaceAllComma};`

    return finalStyleFormat
}

/**
 *
 *  This function can be used to apply styles that are only needed on an iPad devices.
 *
 *  @param {string} {styles} * - The css styles we want to apply.
 *  @param {object} {options} ? - The additional options that we could pass to overwrite the default values.
 *  @param {number} options.min - The min value ( min-width ) from which the media query shall come into effect. Default value is 921.
 *  @param {number} options.max - The max value ( max-width ) within which the media query should be applied. Default value is 1367.
 *  @param {boolean} options.shouldApplyStyle - Apply the style based on the boolean value. Default value is true.
 *  @param {string} options.orientation - Set the orientation for which the style will be applied. Default value is "landscape".
 *  @param {string} options.device - The device type to apply the style. Default value is "ipad".
 *
 *  ( * ) -> required
 *  ( ? ) -> optional
 *
 *  @example
 *   Default format
 *   ${applyIPadOnlyStyle('width:50%,height:100%')}
 *
 *   Style with custom min-width
 *   ${applyIPadOnlyStyle('width:50%,height:100%', { min: 1024 } )}
 *
 *   Apply style only if isArtefactMode is true
 *   ${({ isArtefactMode }) => applyIPadOnlyStyle('width: 18vw; height: 12vw;', { shouldApplyStyle: isArtefactMode })}
 *
 */

const MOBILE_MIN_WIDTH = 320
const MOBILE_MAX_WIDTH = 900
const IPAD_MIN_WIDTH = 921
const IPAD_MAX_WIDTH = 1367
//TODO: name of the util needs to be updated
export const applyIPadOnlyStyle = (styles, options = {}) => {
    const { device = 'ipad' } = options

    const defaultDeviceWidth = (minWidth, maxWidth) =>
        device === 'ipad' ? minWidth : maxWidth

    const {
        min = defaultDeviceWidth(IPAD_MIN_WIDTH, MOBILE_MIN_WIDTH),
        max = defaultDeviceWidth(IPAD_MAX_WIDTH, MOBILE_MAX_WIDTH),
        shouldApplyStyle = true,
        orientation = 'landscape',
    } = options

    const finalStyleFormat = formatStyle(styles)

    const customOrientation =
        orientation === 'both' ? '' : `and (orientation: ${orientation})`

    const customQuery = `@media (min-width: ${min}px) and (max-width: ${max}px) ${customOrientation} and (-webkit-min-device-pixel-ratio: 2)`

    return (
        shouldApplyStyle &&
        css`
            ${customQuery} {
                ${finalStyleFormat}
            }
        `
    )
}
